import React, { useEffect } from 'react';
import { X } from 'lucide-react';

interface ConfirmActionModalProps {
  /** Question naming the exact target, e.g. "Archive Jane Doe?" */
  title: string;
  /** What will happen, and whether it can be undone */
  children: React.ReactNode;
  /** Label for the confirm button. Name the action ("Archive user"), never "OK" */
  confirmLabel: string;
  /** Red confirm button for destructive actions */
  danger?: boolean;
  /** Request in flight: confirm is disabled (no double submit) and the modal can't be dismissed */
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Second click of the back office's two-click confirmation. The action button
 * only opens this modal; the action runs from the confirm button here.
 *
 * Misclick guards: focus starts on "Go back" so a stray Enter backs out, and
 * Esc / backdrop click close the modal without acting.
 */
export default function ConfirmActionModal({
  title,
  children,
  confirmLabel,
  danger = false,
  loading = false,
  error,
  onConfirm,
  onClose,
}: ConfirmActionModalProps) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !loading) onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loading, onClose]);

  return (
    <div
      className="bo-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-action-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="bo-modal" style={{ maxWidth: 480 }}>
        <div className="bo-modal-header">
          <div id="confirm-action-title" className="bo-modal-title">{title}</div>
          <button
            type="button"
            className="bo-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="bo-modal-body">
          <div style={{ lineHeight: 1.6, color: 'var(--color-gray-700)', fontSize: 'var(--font-size-sm)' }}>
            {children}
          </div>

          {error && (
            <div className="bo-error" style={{ marginTop: 12 }}>{error}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button
              type="button"
              className="bo-btn bo-btn-secondary"
              onClick={onClose}
              disabled={loading}
              autoFocus
            >
              Go back
            </button>
            <button
              type="button"
              className={`bo-btn ${danger ? 'bo-btn-danger' : 'bo-btn-primary'}`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? 'Working…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
